/**
 * voice-v8 — die Fortsetzung in der App. (12.10.2026, Bahn „fortsetzung-anhaengen“; Beschreibung: shared/fortsetzung.ts,
 * docs/fortsetzung-2026-10-12.md)
 *
 * Martin spricht oft in Denkpausen. „Absenden nach Stille“ schneidet dann mitten in der Äußerung ab, und bisher gab es dafür kein Anschließen:
 * die Antwort auf den ersten Teil lief, der Knopf war bis zu ihrem Ende gesperrt, der Rest ging als getrennte Nachricht hinaus. Dieser Speicher
 * merkt sich das letzte Auto-Senden und entscheidet, ob eine NEUE Aufnahme binnen `FORTSETZUNG_FENSTER_MS` danach dessen Fortsetzung ist.
 *
 * Er ist rein (keine Netzanfrage, kein DOM): die Seite (frontend/app.ts) ruft ihn und tut, was er sagt — den Knopf freigeben, die Antwort der
 * Vorrunde abbrechen (POST /api/chat/abort), die Wiedergabe stoppen, die Aufnahme mit dem Feld `fortsetzung` senden.
 *
 *   beiSenden   Jedes Absenden einer Aufnahme. Gibt ihre Kennung zurück (die der Server als `anfrageKennung` kennt und als `vorherId` der nächsten
 *               Aufnahme wieder erwartet) und, wenn diese Aufnahme selbst eine Fortsetzung war, deren Angabe. Merkt sich NUR ein Auto-Senden
 *               (Ende nach Stille) als mögliche Vorrunde; jedes andere Absenden vergisst sie (nach einem Knopf ist nichts „abgeschnitten“).
 *   beiStart    Der Beginn einer neuen Aufnahme. Gilt sie als Fortsetzung, steht hier die Angabe und ob die Antwort der Vorrunde noch läuft.
 *   antwortEnde Der Strom der Antwort auf die Vorrunde ist zu Ende (dann braucht es keinen Abbruch mehr).
 *   vergiss     Eine andere Nachricht ging hinaus (getippt …): an die Vorrunde schließt nichts mehr an.
 *
 * ⚠️ Nur ein MENSCH startet eine Fortsetzung: der Knopf, die Aktionstaste, Freihand. Das von selbst geöffnete Mikro (nach Frage/Antwort) und „Hey
 *    Vespera“ nicht — dort ist nicht zu unterscheiden, ob Martin den Satz fortsetzt oder auf Vesperas Antwort reagiert (Aufrufer: `mensch`).
 * ⚠️ `erlaubt` kommt vom Server (Schalter FORTSETZUNG, Antwort von /api/stille-auto). Ohne ausdrückliches Ja gibt es kein Fenster — im Zweifel das alte Verhalten.
 */
import { FORTSETZUNG_FENSTER_MS, type FortsetzungAngabe, type FortsetzungSatz } from "../../shared/fortsetzung.js";

/** Was die Karte beim Auto-Senden wusste — nur Zahlen und die Art des Satzendes. */
export interface AutoSendeZahlen {
  /** Jevs Urteil für den Schlusstext, `null`: keines. */
  jev: number | null;
  satz: FortsetzungSatz;
  /** Die Stille beim Absenden (ms), `null`: unbekannt. */
  stillMs: number | null;
}

interface Sendung {
  kennung: string;
  /** Wann das Auto-Senden begann (Uhr der Seite, ms). */
  ts: number;
  zahlen: AutoSendeZahlen;
  /** Hat der Server die Fortsetzung bei der letzten Antwort gemeldet? */
  erlaubt: boolean;
  /** Läuft die Antwort auf diese Nachricht noch (der Strom ist nicht zu Ende)? */
  antwortLaeuft: boolean;
}

interface FortsetzungStart {
  angabe: FortsetzungAngabe;
  /** Die Antwort der Vorrunde läuft noch: die Seite bricht sie JETZT ab. */
  antwortAbbrechen: boolean;
}

export interface FortsetzungSpeicher {
  /** Ist das Fenster der letzten Vorrunde offen — darf der Knopf trotz laufender Antwort starten? */
  fensterOffen(): boolean;
  beiStart(o: { mensch: boolean; ttsLief: boolean }): FortsetzungStart | null;
  /** `ts`: der Moment der Entscheidung zu senden (Uhr der Seite); ohne Angabe: jetzt. Das Fenster zählt von dort. */
  beiSenden(
    vonSelbst: boolean,
    zahlen: AutoSendeZahlen | null,
    erlaubt: boolean,
    ts?: number,
  ): { kennung: string; fortsetzung: FortsetzungAngabe | null };
  antwortEnde(kennung: string): void;
  verwerfeAktive(): void;
  vergiss(): void;
  /** Für die Zeile im Protokoll: wie lange das Fenster noch offen ist (ms) — `null`, wenn keine Vorrunde da ist. */
  restMs(): number | null;
}

/** Eine Kennung, wie sie `streamPtt` bisher selbst erzeugte (UUID, sonst Zeit und Zufall). */
export function neueAnfrageKennung(): string {
  return globalThis.crypto?.randomUUID?.() ?? `ptt-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function neuerFortsetzungSpeicher(
  jetzt: () => number = () => performance.now(),
  kennung: () => string = neueAnfrageKennung,
): FortsetzungSpeicher {
  let sendung: Sendung | null = null;
  /** Die Angabe der Aufnahme, die gerade läuft — gebunden an den Start, verbraucht vom Absenden. */
  let aktiv: FortsetzungAngabe | null = null;

  const offen = (): boolean => sendung !== null && sendung.erlaubt && jetzt() - sendung.ts <= FORTSETZUNG_FENSTER_MS;

  return {
    fensterOffen: offen,

    beiStart({ mensch, ttsLief }) {
      aktiv = null;
      if (!mensch || sendung === null || !offen()) return null;
      const angabe: FortsetzungAngabe = {
        vorherId: sendung.kennung,
        abstandMs: Math.max(0, Math.round(jetzt() - sendung.ts)),
        jev: sendung.zahlen.jev,
        satz: sendung.zahlen.satz,
        stillMs: sendung.zahlen.stillMs,
        tts: ttsLief ? "lief" : "nein",
      };
      const antwortAbbrechen = sendung.antwortLaeuft;
      sendung.antwortLaeuft = false;
      aktiv = angabe;
      return { angabe, antwortAbbrechen };
    },

    beiSenden(vonSelbst, zahlen, erlaubt, ts) {
      const fortsetzung = aktiv;
      aktiv = null;
      const k = kennung();
      sendung = vonSelbst
        ? {
            kennung: k,
            ts: ts ?? jetzt(),
            zahlen: zahlen ?? { jev: null, satz: "-", stillMs: null },
            erlaubt,
            antwortLaeuft: true,
          }
        : null;
      return { kennung: k, fortsetzung };
    },

    antwortEnde(k) {
      if (sendung?.kennung === k) sendung.antwortLaeuft = false;
    },

    verwerfeAktive() {
      aktiv = null;
    },

    vergiss() {
      sendung = null;
      aktiv = null;
    },

    restMs() {
      return sendung === null ? null : Math.max(0, Math.round(FORTSETZUNG_FENSTER_MS - (jetzt() - sendung.ts)));
    },
  };
}

/**
 * Der Beginn einer neuen Aufnahme — alles, was die Fortsetzung dabei tut, an EINER Stelle (und darum ohne die ganze Seite prüfbar):
 *
 *   1. die Wiedergabe der Vorrunde stoppen (`stoppeWiedergabe`, in der Seite `stopCurrentTts` — sie läuft bei jedem Druck und bricht ALLE Schlangen ab;
 *      eine abgebrochene Schlange nimmt kein spätes Textstück mehr an);
 *   2. dem Speicher sagen, dass eine Aufnahme beginnt (`beiStart`): gilt sie als Fortsetzung, steht dort die Angabe;
 *   3. läuft die Antwort der Vorrunde noch, sie SOFORT abbrechen (`abbrechen`, in der Seite POST /api/chat/abort) — im selben Aufruf, nicht erst
 *      beim Senden und nicht erst, wenn das Mikro da ist.
 *
 * Die Reihenfolge ist die der Seite: erst stoppen (und vorher lesen, ob etwas klang: `ttsLief` kommt vom Aufrufer, gelesen VOR dem Stopp),
 * dann der Speicher, dann der Abbruch. Liefert die Angabe — oder `null`, wenn die Aufnahme keine Fortsetzung ist.
 */
export function fortsetzungBeiAufnahmestart(o: {
  speicher: FortsetzungSpeicher;
  /** Hat ein Mensch gestartet (Knopf, Aktionstaste)? Das von selbst geöffnete Mikro und „Hey Vespera“ nicht. */
  mensch: boolean;
  /** Klang die Wiedergabe, bevor sie gestoppt wurde? */
  ttsLief: boolean;
  stoppeWiedergabe: () => void;
  abbrechen: () => void;
  melde: (zeile: string) => void;
}): FortsetzungAngabe | null {
  o.stoppeWiedergabe();
  const fort = o.speicher.beiStart({ mensch: o.mensch, ttsLief: o.ttsLief });
  if (!fort) return null;
  o.melde(`[fortsetzung-app] start abstandMs=${fort.angabe.abstandMs} antwortLief=${fort.antwortAbbrechen ? "ja" : "nein"} tts=${fort.angabe.tts}`);
  if (fort.antwortAbbrechen) o.abbrechen();
  return fort.angabe;
}
